<template>
  <div class="post-page">
    <!-- 顶部导航栏 -->
    <NavBar :title="t('post.create.title')" :showBack="true" />

    <!-- 输入区域 -->
    <div class="input-area">
      <div class="title-input-wrapper">
        <input
          v-model="title"
          class="title-input"
          type="text"
          maxlength="80"
          :placeholder="t('post.create.titlePlaceholder')"
        />
        <span class="title-count">{{ title.length }}/80</span>
      </div>
      <RichEditor
        v-model:model-value="content"
        :placeholder="t('post.create.contentPlaceholder')"
        :isPost="true"
      />

      <!-- 标签选择区域 -->
      <TagList
        :tags="threadStore.tagList"
        mode="select"
        :selected-tag-ids="selectedTagIds"
        @tag-toggle="toggleTag"
      />
    </div>

    <!-- 底部工具栏 -->
    <PostToolbar
      :showSelectBoard="true"
      :showSubmit="true"
      @submit="handleSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onActivated } from "vue";
import NavBar from "@/components/common/NavBar.vue";
import PostToolbar from "@/components/PostToolbar.vue";
import RichEditor from "@/components/common/RichEditor.vue";
import TagList from "@/components/common/TagList.vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  useThreadStore,
  useForumStore,
  useUserStore,
  useToastStore,
} from "@/stores";
import { clickButton, pageType } from "@/utils/point";
import { extractRichContent } from "../utils/richEditor";
import { getDeviceLanguage } from "@/utils";
const { t, locale } = useI18n();
const title = ref("");
const content = ref("");
const toast = useToastStore();
const threadStore = useThreadStore();
const router = useRouter();
const route = useRoute();
const forumStore = useForumStore();
const userStore = useUserStore();
// 添加标志位，确保 setSelectedBoard 只执行一次
const hasSetSelectedBoard = ref(false);
// 选中的标签ID数组
const selectedTagIds = ref<string[]>([]);
const handleSubmit = async () => {
  if (title.value.trim() === "") {
    toast.showToast(t("post.create.titleEmpty"), "error");
    return;
  }
  if (content.value.trim() === "") {
    toast.showToast(t("post.create.contentEmpty"), "error");
    return;
  }

  // 如果官方账号，则直接发布
  if (userStore.isOfficialAccount) {
    await submitThread(title.value, content.value);
    return;
  }

  // 提取并过滤内容
  const extractedContent = extractRichContent(content.value);
  // 检查纯文本长度（去除HTML标签）
  const plainText = extractedContent.content.replace(/<[^>]*>/g, "").trim();
  if (plainText.length < 5) {
    toast.showToast(t("postDetail.commentTooShort"), "error", 2000);
    return;
  }
  // 提交过滤后的安全内容
  await submitThread(title.value, extractedContent.content);
};

// 切换标签选中状态
const toggleTag = (tagId: number | string) => {
  const index = selectedTagIds.value.indexOf(tagId as string);
  if (index > -1) {
    selectedTagIds.value.splice(index, 1);
  } else {
    selectedTagIds.value.push(tagId as string);
  }
};

const submitThread = async (titleText: string, contentText: string) => {
  toast.showToast(t("post.create.submitting"), "loading");
  try {
    clickButton({
      eventType: pageType.POST,
      eventReason: "点击发布",
    });
    // 将选中的标签ID用英文逗号拼接
    const tagIds =
      selectedTagIds.value.length > 0 ? selectedTagIds.value.join(",") : "";
    const res = await threadStore.createThread(
      threadStore.selectedBoardFid as number,
      tagIds,
      titleText,
      contentText,
      getDeviceLanguage()
    );
    if (res.code == 200) {
      toast.showToast(t("post.create.success"), "success", 1000);
      await forumStore.getForumInfo();
      title.value = "";
      content.value = "";
      selectedTagIds.value = [];
      // 切换到选中的板块
      const selectedBoardIndex = forumStore.forumSections.findIndex(
        (section) => section.fid === threadStore.selectedBoardFid
      );
      if (selectedBoardIndex !== -1) {
        forumStore.switchSection(selectedBoardIndex);
      }
      router.push(`/`);
    } else {
      toast.showErrorCodeToast(+res?.code, t);
    }
  } catch (error) {
    toast.showToast(t("post.create.failed"), "error", 2000);
    return;
  }
};

// 自动选择标签的函数
const autoSelectTags = () => {
  // 如果标签列表还未加载，等待加载完成
  if (!threadStore.tagList || threadStore.tagList.length === 0) {
    return;
  }

  const autoTagIds = route.query.autoTagIds as string;

  // 只有在有路由参数时才自动选择
  if (!autoTagIds) {
    return;
  }

  // 处理标签ID（单个或多个，都用逗号分隔）
  const tagIdArray = autoTagIds.split(",");
  tagIdArray.forEach((tagId) => {
    const trimmedTagId = tagId.trim();
    // 检查标签是否存在于标签列表中
    const tagExists = threadStore.tagList.some(
      (tag) => tag.id.toString() === trimmedTagId
    );
    if (tagExists && !selectedTagIds.value.includes(trimmedTagId)) {
      selectedTagIds.value.push(trimmedTagId);
    }
  });
};

onActivated(async () => {
  // 如果 forumSections 已经加载完成但还没有设置选中板块，则重新初始化
  if (forumStore.forumSections.length > 0 && !hasSetSelectedBoard.value) {
    initializeBoardSelection();
  }
  // 清空之前的选择
  selectedTagIds.value = [];
  await threadStore.getTagList();
  // 延迟执行自动选择，确保标签列表已加载
  setTimeout(() => {
    autoSelectTags();
  }, 100);
});

// 抽取板块选择逻辑为独立函数
const initializeBoardSelection = () => {
  // 检查用户登录状态和板块数量
  if (!userStore.userInfo?.uid || forumStore.forumSections.length <= 1) {
    router.push("/");
    return;
  }

  // 如果已经设置过选中板块，则不再重复设置
  if (hasSetSelectedBoard.value) {
    return;
  }

  // 1.过滤掉推荐板块
  // 2.如果不是官方账号，过滤掉guide、news板块
  const sections = forumStore.forumSections.filter(
    (item) => item.fid != "trending"
  );
  if (userStore.isOfficialAccount) {
    threadStore.setSelectedBoardList(sections);
  } else {
    //  只展示CHATS
    threadStore.setSelectedBoardList(
      sections.filter((item) => item.name === "chats")
    );
  }

  // 设置选中板块并标记为已设置
  if (threadStore.selectedBoardList.length > 0) {
    threadStore.setSelectedBoard(threadStore.selectedBoardList[0]);
    hasSetSelectedBoard.value = true;
  }
};
</script>

<style scoped>
.post-page {
  padding-top: 56px;
  min-height: 100vh; /* 降级方案，支持旧浏览器 */
  min-height: 100dvh; /* 现代浏览器，动态视口高度 */
  background: #fff;
  display: flex;
  flex-direction: column;
}
.input-area {
  padding: 16px 16px 0 16px;
  height: calc(100vh - 120px); /* 降级方案，支持旧浏览器 */
  height: calc(100dvh - 120px); /* 现代浏览器，动态视口高度 */
  display: flex;
  flex-direction: column;
}
.title-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.title-input-wrapper:hover,
.title-input-wrapper:focus-within {
  border-color: #4a90e2;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
}

.title-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  color: #222;
  font-weight: 500;
  padding: 0;
}

.title-input::placeholder {
  color: #999;
  font-weight: normal;
}

.title-count {
  color: #999;
  font-size: 14px;
  margin-left: 12px;
  user-select: none;
}

.tag-list-wrapper {
  margin-top: 16px;
  padding-bottom: 8px;
}
</style>
